import { generateAnswerRelevance, generateQuestionOrder, WizardState } from '___store'
import {
  Answers,
  DataStructure,
  DOCUMENT_FLOW_MODES,
  DocumentProgress,
  Integrations,
  Footer,
  Header,
  ITemplate,
  LanguageValue,
  QuestionLayout,
  Questions,
  Segment,
} from '___types'
import { rasterizeDataStructure } from 'Layouts/WizardLayout/components/RightPane/RightPane.Editor.Components'

// ====================== IMPLEMENT TYPESCRIPT ====================== //
import { parseV3forBE } from 'Wizard/parsing'
// ================================================================== //

import useApproveDocument from './useApproveDocument'
import useCreateDocument from './useCreateDocument'
import useCreateDocumentFolder from './useCreateDocumentFolder'
import useDownloadDocument from './useDownloadDocument'
import useDuplicateDocument from './useDuplicateDocument'
import useFetchDocument from './useFetchDocument'
import useFetchDocumentFolderList from './useFetchDocumentFolderList'
import useFetchDocumentList from './useFetchDocumentList'
import useFetchTrashedDocumentList from './useFetchTrashedDocumentList'
import useGenerateDocumentName from './useGenerateDocumentName'
import usePublishDocument from './usePublishDocument'
import useRemoveDocument from './useRemoveDocument'
import useRemoveDocumentFolder from './useRemoveDocumentFolder'
import useRequestDocumentSignature from './useRequestDocumentSignature'
import useSendDocumentSignatureReminder from './useSendDocumentSignatureReminder'
import useShareDocument from './useShareDocument'
import useSubmitDocument from './useSubmitDocument'
import useUpdateDocument from './useUpdateDocument'
import useUpdateDocumentFolder from './useUpdateDocumentFolder'
import useUploadPDFDocument from './useUploadPDFDocument'

export type { ApproveDocumentVariables, ApproveDocumentFunctionType } from './useApproveDocument'
export type { CreateDocumentVariables, CreateDocumentFunctionType } from './useCreateDocument'
export type { CreateDocumentFolderVariables, CreateDocumentFolderFunctionType, CreateDocumentFolderMutationOptions } from './useCreateDocumentFolder'
export type { DownloadRegularDocumentVariables, DownloadRegularDocumentFunctionType } from './useDownloadDocument'
export type { DownloadPDFDocumentVariables, DownloadPDFDocumentFunctionType } from './useDownloadDocument'
export type { DownloadSignedDocumentVariables, DownloadSignedDocumentFunctionType } from './useDownloadDocument'
export type { DuplicateDocumentVariables, DuplicateDocumentFunctionType } from './useDuplicateDocument'
export type { PublishDocumentVariables, PublishDocumentFunctionType } from './usePublishDocument'
export type { RemoveDocumentVariables, RemoveDocumentFunctionType } from './useRemoveDocument'
export type { RemoveDocumentFolderVariables, RemoveDocumentFolderFunctionType } from './useRemoveDocumentFolder'
export type { RequestRegularDocumentSignatureVariables, RequestRegularDocumentSignatureFunctionType } from './useRequestDocumentSignature'
export type { RequestPDFDocumentSignatureVariables, RequestPDFDocumentSignatureFunctionType } from './useRequestDocumentSignature'
export type { SendDocumentSignatureReminderVariables, SendDocumentSignatureReminderFunctionType } from './useSendDocumentSignatureReminder'
export type { ShareDocumentVariables, ShareDocumentFunctionType, UnshareDocumentVariables, UnshareDocumentFunctionType } from './useShareDocument'
export type { SubmitDocumentVariables, SubmitDocumentFunctionType } from './useSubmitDocument'
export type { UpdateDocumentVariables, UpdateDocumentFunctionType } from './useUpdateDocument'
export type { UpdateDocumentFolderVariables, UpdateDocumentFolderFunctionType } from './useUpdateDocumentFolder'
export type { UploadPDFDocumentVariables, UploadPDFDocumentFunctionType } from './useUploadPDFDocument'

const keyMap = { tableHeader: 'header', tableBody: 'body', tableFooter: 'footer', customStyle: 'styleName' } as Record<string, string>
const keyFilter = ['tag', 'start', 'length']
const keyIgnore = ['id', 'styles']
const parseSegmentsForDownload = (segments: Segment[]) =>
  segments.map(segment =>
    Object.entries(segment).reduce((acc, [key, value]) => {
      if (keyFilter.includes(key)) return acc
      const mapped = (
        keyIgnore.includes(key) ? { [key]: value } : { [keyMap[key] || key]: Array.isArray(value) ? parseSegmentsForDownload(value) : value }
      ) as Record<string, unknown>
      return Object.assign(acc, mapped)
    }, {} as Segment)
  )
export const parseHeadersFooters = <T extends 'headers' | 'footers'>(key: T, dataStructure: DataStructure, locationParentIds: string[]) =>
  dataStructure[key]?.reduce(
    (result, entry) =>
      result.concat(
        locationParentIds.includes(entry.id) || entry.segments?.map(({ id }) => String(id)).some((id: string) => locationParentIds.includes(id))
          ? Object.assign({}, entry, { segments: parseSegmentsForDownload(entry.segments) })
          : []
      ),
    [] as Header[] | Footer[]
  ) as T extends 'headers' ? Header[] : Footer[] | undefined

export const parseAnswersForDownload = (
  template: ITemplate,
  languages?: LanguageValue[],
  integrations?: Integrations | null,
  split?: string | null,
  instancing?: Record<string, number>,
  answers?: Answers | null
): [Segment[], Record<string, unknown>[], Record<string, unknown>[]] => {
  const { dataStructure, questions, questionLayout, locations, dependencies } = template
  const [evaluatedDataStructure, changedHeaders, changedFooters] = rasterizeDataStructure(
    DOCUMENT_FLOW_MODES.PREVIEW,
    dataStructure,
    locations,
    questions,
    questionLayout,
    languages,
    integrations,
    split,
    instancing,
    answers,
    dependencies
  )
  const parsedDataStructure = (parseV3forBE({ dataStructure: evaluatedDataStructure }) as ITemplate).dataStructure
  const parsedHeaders = changedHeaders.map(header =>
    Object.assign(header, { segments: parseSegmentsForDownload((parseV3forBE({ dataStructure: header }) as ITemplate).dataStructure.segments) })
  )
  const parsedFooters = changedFooters.map(footer =>
    Object.assign(footer, { segments: parseSegmentsForDownload((parseV3forBE({ dataStructure: footer }) as ITemplate).dataStructure.segments) })
  )
  return [parseSegmentsForDownload(parsedDataStructure.segments), parsedHeaders, parsedFooters]
}

export const generateDocumentProgress = (
  questionLayout: QuestionLayout,
  instancing: Record<string, number> | null,
  questions: Questions,
  answers?: Answers
) => {
  const pseudoState = { questions: questions, answers: answers } as WizardState
  const answerRelevance = generateAnswerRelevance(pseudoState)
  const questionOrder = generateQuestionOrder(questionLayout, instancing, questions, answerRelevance)
  return questionOrder.reduce((result, questionOrderEntry) => {
    const questionId = questionOrderEntry.split(':')[1]
    const question = questions.find(({ id }) => id === questionId)
    if (!question) return result
    const key = question.isPrivate ? 'private' : 'public'
    const resultingQuestionCount = (result[key]?.questions || 0) + 1
    const resultingAnsweredCount = (result[key]?.answered || 0) + Number(Boolean((answers || []).find(({ id }) => id === questionId)))
    Object.assign(result, { [key]: { questions: resultingQuestionCount, answered: resultingAnsweredCount } })
    return result
  }, {} as DocumentProgress)
}

export {
  useApproveDocument,
  useCreateDocument,
  useCreateDocumentFolder,
  useDownloadDocument,
  useDuplicateDocument,
  useFetchDocument,
  useFetchDocumentFolderList,
  useFetchDocumentList,
  useFetchTrashedDocumentList,
  useGenerateDocumentName,
  usePublishDocument,
  useRemoveDocument,
  useRemoveDocumentFolder,
  useRequestDocumentSignature,
  useSendDocumentSignatureReminder,
  useShareDocument,
  useSubmitDocument,
  useUpdateDocument,
  useUpdateDocumentFolder,
  useUploadPDFDocument,
}
